import { CodeFormat } from "@web-app/types";

/** Feature flag that shows codes to scan, and their scans, in the web app. */
export const SCANNABLE_CODE_FEATURE = "scannableCode";

/** Used when neither the code nor its workspace names a format (web-api's DEFAULT_CODE_FORMAT). */
export const DEFAULT_CODE_FORMAT: CodeFormat = "QR";

/** Formats web-api accepts, as offered when entering codes by hand. */
export const CODE_FORMAT_OPTIONS: { label: string; value: CodeFormat }[] = [
  { label: "QR Code", value: "QR" },
  { label: "Data Matrix", value: "DATA_MATRIX" },
  { label: "Code 128", value: "CODE_128" },
  { label: "UPC", value: "UPC" },
];

const CODE_FORMAT_LABELS = Object.fromEntries(
  CODE_FORMAT_OPTIONS.map((option) => [option.value, option.label]),
) as Record<CodeFormat, string>;

/** A format's display name, e.g. "Code 128"; unknown formats are shown as sent. */
export const codeFormatLabel = (format: string): string =>
  CODE_FORMAT_LABELS[format as CodeFormat] ?? format;

/** Resolves a code's format: its own, else the workspace default, else QR. */
export function resolveCodeFormat(
  format?: string | null,
  workspaceDefault?: string | null,
): string {
  return format?.trim() || workspaceDefault?.trim() || DEFAULT_CODE_FORMAT;
}

const BWIP_BCIDS: Record<CodeFormat, string> = {
  CODE_128: "code128",
  DATA_MATRIX: "datamatrix",
  QR: "qrcode",
  UPC: "upca",
};

/** Maps a code format to a bwip-js encoder id, or null if unsupported. */
export function toBwipBcid(format: string): string | null {
  return BWIP_BCIDS[format as CodeFormat] ?? null;
}

type BwipModule = typeof import("bwip-js/browser");

let bwipModule: Promise<BwipModule> | null = null;

// bwip-js is about 1MB, so it is only loaded once a barcode is shown.
function loadBwip(): Promise<BwipModule> {
  bwipModule ??= import("bwip-js/browser");
  return bwipModule;
}

export type RenderedScannableCode = {
  bcid: string;
  /** Size in CSS pixels at which modules land on whole pixels. */
  height: number;
  svg: string;
  width: number;
};

// Sized so the code can be scanned off a screen, e.g. with the driver app:
// - 2D codes: 3px (QR) or 4px (Data Matrix, whose symbols are small)
//   modules, with the quiet zone the spec asks for (4 and 1 modules).
// - Linear codes: 2px narrow bars, bars about 1.5cm tall, and a 10-module
//   (9 for UPC) quiet zone on each side.
// Padding is in bwip-js points; one point is scale px, and a QR/Data Matrix
// module is 2 points, a linear module 1.
const RENDER_OPTIONS: Record<string, Record<string, number>> = {
  code128: { height: 10, paddingheight: 2, paddingwidth: 10, scale: 2 },
  datamatrix: { paddingheight: 2, paddingwidth: 2, scale: 2 },
  qrcode: { paddingheight: 8, paddingwidth: 8, scale: 1.5 },
  upca: { height: 10, paddingheight: 2, paddingwidth: 9, scale: 2 },
};

/**
 * Renders text as an SVG barcode in the given format, at a size that scans
 * reliably (see RENDER_OPTIONS). Falls back to a QR code, which can encode any
 * text, when the format is unknown or can't encode the text (e.g. letters in a
 * UPC), and resolves to null if even that fails.
 */
export async function renderScannableCodeSvg(
  text: string,
  format: string,
): Promise<RenderedScannableCode | null> {
  const bwip = await loadBwip();
  const requested = toBwipBcid(format);
  const candidates = [requested, "qrcode"].filter(
    (bcid, index, all): bcid is string => !!bcid && all.indexOf(bcid) === index,
  );

  for (const bcid of candidates) {
    try {
      const svg = bwip.toSVG({
        backgroundcolor: "FFFFFF",
        bcid,
        text,
        ...RENDER_OPTIONS[bcid],
      });
      // bwip-js only sets a viewBox, in pixels at the given scale.
      const [, , width, height] = (svg.match(/viewBox="([^"]+)"/)?.[1] ?? "")
        .split(/\s+/)
        .map(Number);
      if (!width || !height) continue;
      return { bcid, height, svg, width };
    } catch {
      // Try the next candidate.
    }
  }
  return null;
}
